import React, { useEffect, useState } from 'react';
import { TranslationUtils } from '../../utils';
import { CancellationErrorModal, CancellationSummary as CancellationSummaryComponent, ConfirmCancelModal, Loading } from '../../components';
import { TripService } from '../../services/trip/tripService';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import ddLogger from '../../logging/ddLogger';
import { CancellationSummary } from 'guest-data-types-ts-lib';
import { useTripStoreUpdater } from '../../hooks';
import './Cancellation.scss';
import { setReservationIsCancelled } from '../../store/actions';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from '../../routes';
import segmentService, { Step } from '../../services/segment/segmentService';

export const Cancellation: React.FC = () => {
    const { reservation } = useSelector((state: StoreState) => state.trip);
    const [cancellationSummary, setCancellationSummary] = useState<CancellationSummary | undefined>(undefined);
    const [isFetchingPreview, setIsFetchingPreview] = useState<boolean>(false);
    const [isPersistingCancel, setIsPersistingCancel] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [isShowingConfirmModal, setIsShowingConfirmModal] = useState<boolean>(false);

    const NOT_IMPLEMENTED_STATUS_CODE = 501;
    const INTERNAL_SERVER_ERROR_STATUS_CODE = 500;

    const history = useHistory();
    const dispatch = useDispatch();

    useTripStoreUpdater(true);

    useEffect(() => {
        if (reservation?.id) {
            getCancellationPreview(reservation.id);
        }
    }, [reservation]);

    const getCancellationPreview = async (resId: string) => {
        try {
            setError(undefined);
            setIsFetchingPreview(true);
            const { attributes: summary } = await new TripService().getAdjustmentPreview(resId, { name: 'cancellation' });

            setCancellationSummary(summary as CancellationSummary);
        } catch (error) {
            ddLogger.error(`error fetching cancellation preview for res ${resId}`, {
                error,
            });
            setError(error);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    const persistCancellation = async (resId: string, summary: CancellationSummary) => {
        try {
            setError(undefined);
            setIsPersistingCancel(true);

            const response = await new TripService().persistAdjustment(resId, {
                name: 'cancellation',
                uuid: summary.uuid!,
            });

            segmentService.trackAdjustmentSuccess(Step.CANCELLATION, response.attributes as CancellationSummary, {});
            dispatch(setReservationIsCancelled(true));
            ddLogger.info(`cancellation persisted for reservation ${resId}`, { summary, response });
        } catch (error) {
            ddLogger.error(`error persisting cancellation for res ${resId}`, {
                error,
            });
            setError(error);
        } finally {
            setIsShowingConfirmModal(false);
            setIsPersistingCancel(false);
        }
    };

    const handleCloseErrorModal = () => {
        history.push(AppRoutes.RESERVATION);
    };
    const handleConfirmCancel = () => {
        setIsShowingConfirmModal(true);
    };

    const handleDismissModal = () => {
        setIsShowingConfirmModal(false);
    };

    if (isFetchingPreview || isPersistingCancel) {
        return (
            <div>
                <Loading />
            </div>
        );
    }
    return (
        <React.Fragment>
            <div>
                <h1 tabIndex={0} className="heading-large page-title">
                    {TranslationUtils.get('cancel_trip_title')}
                </h1>
            </div>

            <div>
                {cancellationSummary && (
                    <React.Fragment>
                        <CancellationSummaryComponent summary={cancellationSummary} onConfirmCancel={handleConfirmCancel} />

                        <ConfirmCancelModal
                            isShowing={isShowingConfirmModal}
                            summary={cancellationSummary}
                            onConfirm={(summary) => persistCancellation(reservation?.id!, summary)}
                            onDismiss={handleDismissModal}
                        />
                    </React.Fragment>
                )}
            </div>

            <CancellationErrorModal show={!!error} handleClose={handleCloseErrorModal} />
        </React.Fragment>
    );
};
